import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { BRAND_COLORS } from '../../../../../../constants/styles';
import { METRIC_HIGHER_IS_BETTER, STATUS_COLORS } from '../../../../constants';
import { showRateHistoryConfig } from '../../../config/metricHistory';
import { IModalFilterContext, IShowRateDetailsData } from '../../../types';
import { formatActiveStatuses } from '../../../utils/formatActiveStatuses';
import { ExplainerBox } from '../../shared/ExplainerBox';
import { FormulaBox } from '../../shared/FormulaBox';
import { FormulaLine } from '../../shared/FormulaLine';
import { MetricDetailsModal } from '../../shared/MetricDetailsModal';
import { MetricHistoryChart } from '../../shared/MetricHistoryChart';
import { MetricsDivider } from '../../shared/MetricsDivider';
import { StatRow } from '../../shared/StatRow';
import { ThresholdLegend } from '../../shared/ThresholdLegend';

interface IShowRateDetailsModalProps {
  visible: boolean;
  onClose: () => void;
  data: IShowRateDetailsData | null;
  filter: IModalFilterContext;
  donorDonationFrequency?: number;
  donorEligibilityDays?: number;
  historyDataCutoff?: string;
}

export const ShowRateDetailsModal: FC<IShowRateDetailsModalProps> = ({
  visible,
  onClose,
  data,
  filter,
  donorDonationFrequency,
  donorEligibilityDays,
  historyDataCutoff,
}) => {
  const { t } = useTranslation('dashboard.RecruiterDashboard.showRateModal');
  if (!data) return null;

  return (
    <MetricDetailsModal
      visible={visible}
      onClose={onClose}
      data={data}
      title={t('title')}
      tooltip={t('tooltip', {
        statuses: formatActiveStatuses(filter.activeDonorStatuses),
      })}
      sectionLabel={t('sectionCurrentPeriod')}
      headerValue={`${data.showRate}%`}
      explainer={
        <ExplainerBox>
          <strong style={{ color: '#fff' }}>{t('explainerTerm')}</strong>{' '}
          {t('explainerDef')}
        </ExplainerBox>
      }
      historySection={
        <MetricHistoryChart
          config={showRateHistoryConfig}
          live={data}
          filter={filter}
          extra={{
            donorDonationFrequency,
            donorEligibilityDays,
            historyDataCutoff,
          }}
          translationNs="dashboard.RecruiterDashboard.showRateModal"
        />
      }
    >
      <StatRow
        label={t('statDonationsGiven')}
        value={data.donorsWhoDonated}
        color={STATUS_COLORS.good.fill}
      />
      <StatRow
        label={t('statExpected')}
        value={data.expectedDonations}
        color={BRAND_COLORS.secondary.lightSage}
      />
      <MetricsDivider />
      <FormulaBox>
        <FormulaLine>{t('formula')}</FormulaLine>
        <FormulaLine marginTop={4}>
          = {data.donorsWhoDonated} ÷ {data.expectedDonations} × 100
          {' = '}
          <span style={{ color: data.statusColor, fontWeight: 700 }}>
            {data.showRate}%
          </span>
        </FormulaLine>
      </FormulaBox>
      <ThresholdLegend
        breakpoints={data.breakpoints}
        higherIsBetter={METRIC_HIGHER_IS_BETTER.showRate}
        labels={{
          low: t('thresholdLow'),
          mid: t('thresholdMid'),
          high: t('thresholdHigh'),
        }}
      />
    </MetricDetailsModal>
  );
};
